import { Card } from "@components/cards/card";
import { Input } from "@components/forms/input";
import { InputGroup } from "@components/forms/input-group";
import { Select } from "@components/forms/select";
import { InfrastructureCard } from "@components/layout/infrastructure/infrastructure-card";
import { InfrastructureSummaryCard } from "@components/layout/infrastructure/infrastructure-summary-card";
import { InfrastructureTable } from "@components/layout/infrastructure/infrastructure-table";
import { Pagination } from "@components/pagination";
import {
  InfraHostCompanyEnum,
  InfrastructureListItem,
  InfrastructureListQuery,
  InfrastructureListResponse,
  InfrastructureStatusEnum,
  InfrastructureStatusTypes,
  InfraTypeEnum,
} from "@hooks/dtos/IInfrastructureDTO";
import {
  infraHostOptions,
  infrastructureStatusOptions,
  infraTypeOptions,
} from "@hooks/functions/Infrastructure";
import { Masks } from "@hooks/functions/Masks/Masks";
import { api } from "@service/api";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { FaFilter, FaMicrochip, FaServer } from "react-icons/fa";
import {
  FiChevronDown,
  FiChevronUp,
  FiCpu,
  FiGrid,
  FiHardDrive,
  FiList,
  FiRefreshCw,
  FiSearch,
} from "react-icons/fi";

const masks = new Masks();
const DEFAULT_PAGE = 1;
const DEFAULT_TAKE = 9;

type InfrastructureViewMode = "cards" | "list";
type InfrastructureSortMode = "name" | "status" | "type" | "host" | "disk";

interface IInfrastructureFilters {
  search: string;
  typeId: string;
  hostId: string;
  status: string;
  region: string;
  instanceType: string;
  minVCpu: string;
  maxVCpu: string;
  minRamGb: string;
  maxRamGb: string;
  minDiskGb: string;
  maxDiskGb: string;
}

const initialFilters: IInfrastructureFilters = {
  search: "",
  typeId: "",
  hostId: "",
  status: "",
  region: "",
  instanceType: "",
  minVCpu: "",
  maxVCpu: "",
  minRamGb: "",
  maxRamGb: "",
  minDiskGb: "",
  maxDiskGb: "",
};

const parseNumberFilter = (value: string) =>
  value ? Number(value) : undefined;

const compareText = (a?: string, b?: string) =>
  (a || "").localeCompare(b || "", "pt-BR");

export function InfrastructureList() {
  const [filters, setFilters] =
    useState<IInfrastructureFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<IInfrastructureFilters>(initialFilters);
  const [infrastructureList, setInfrastructureList] =
    useState<InfrastructureListResponse>();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<InfrastructureViewMode>("cards");
  const [sortMode, setSortMode] = useState<InfrastructureSortMode>("name");

  const infrastructures = infrastructureList?.infrastructures || [];

  const sortedInfrastructures = useMemo(() => {
    return [...infrastructures].sort((a, b) => {
      switch (sortMode) {
        case "status":
          return compareText(a.status, b.status);
        case "type":
          return compareText(a.typeData?.name, b.typeData?.name);
        case "host":
          return compareText(a.hostData?.name, b.hostData?.name);
        case "disk":
          return (b.diskGb || 0) - (a.diskGb || 0);
        case "name":
        default:
          return compareText(a.name, b.name);
      }
    });
  }, [infrastructures, sortMode]);

  const activeInfrastructures = useMemo(
    () =>
      infrastructures.filter(
        (infrastructure) =>
          infrastructure.status === InfrastructureStatusEnum.ACTIVE
      ).length,
    [infrastructures]
  );

  const maintenanceInfrastructures = useMemo(
    () =>
      infrastructures.filter(
        (infrastructure) =>
          infrastructure.status === InfrastructureStatusEnum.MAINTENANCE ||
          infrastructure.status === InfrastructureStatusEnum.PROVISIONING
      ).length,
    [infrastructures]
  );

  const totalVCpu = useMemo(
    () =>
      infrastructures.reduce(
        (total, infrastructure) => total + (infrastructure.vCpu || 0),
        0
      ),
    [infrastructures]
  );

  const totalDiskGb = useMemo(
    () =>
      infrastructures.reduce(
        (total, infrastructure) => total + (infrastructure.diskGb || 0),
        0
      ),
    [infrastructures]
  );

  const summaryMetrics = useMemo(
    () => [
      {
        title: "Infraestruturas",
        value: isLoading
          ? "..."
          : masks.formatMilhar(infrastructureList?.total || 0),
        subtitle: `${masks.formatMilhar(
          activeInfrastructures
        )} ativas nesta página`,
        icon: FaServer,
        variant: "primary" as const,
      },
      {
        title: "Em atenção",
        value: isLoading
          ? "..."
          : masks.formatMilhar(maintenanceInfrastructures),
        subtitle: "Manutenção ou provisionamento",
        icon: FaFilter,
        variant: "warning" as const,
      },
      {
        title: "vCPUs alocadas",
        value: isLoading ? "..." : masks.formatMilhar(totalVCpu),
        subtitle: "Soma dos registros exibidos",
        icon: FiCpu,
        variant: "info" as const,
      },
      {
        title: "Disco total",
        value: isLoading ? "..." : `${masks.formatMilhar(totalDiskGb)} GB`,
        subtitle: "Capacidade dos registros exibidos",
        icon: FiHardDrive,
        variant: "success" as const,
      },
    ],
    [
      activeInfrastructures,
      infrastructureList?.total,
      isLoading,
      maintenanceInfrastructures,
      totalDiskGb,
      totalVCpu,
    ]
  );

  useEffect(() => {
    async function getInfrastructures() {
      try {
        setIsLoading(true);

        const params: InfrastructureListQuery = {
          page,
          take: DEFAULT_TAKE,
          search: appliedFilters.search || undefined,
          typeId: parseNumberFilter(appliedFilters.typeId) as
            | InfraTypeEnum
            | undefined,
          hostId: parseNumberFilter(appliedFilters.hostId) as
            | InfraHostCompanyEnum
            | undefined,
          status:
            (appliedFilters.status as InfrastructureStatusTypes) || undefined,
          region: appliedFilters.region || undefined,
          instanceType: appliedFilters.instanceType || undefined,
          minVCpu: parseNumberFilter(appliedFilters.minVCpu),
          maxVCpu: parseNumberFilter(appliedFilters.maxVCpu),
          minRamGb: parseNumberFilter(appliedFilters.minRamGb),
          maxRamGb: parseNumberFilter(appliedFilters.maxRamGb),
          minDiskGb: parseNumberFilter(appliedFilters.minDiskGb),
          maxDiskGb: parseNumberFilter(appliedFilters.maxDiskGb),
        };

        const result = await api.get<InfrastructureListResponse>(
          "/customer/infrastructure/list",
          {
            params,
          }
        );

        setInfrastructureList(result.data);
      } catch (err: any) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }

    getInfrastructures();
  }, [appliedFilters, page]);

  function handleFilterChange(field: keyof IInfrastructureFilters, value: string) {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value,
    }));
  }

  function handleSelectFilterChange(
    field: keyof IInfrastructureFilters,
    value: string
  ) {
    const nextFilters = {
      ...filters,
      [field]: value,
    };

    setFilters(nextFilters);
    setPage(DEFAULT_PAGE);
    setAppliedFilters(nextFilters);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPage(DEFAULT_PAGE);
    setAppliedFilters(filters);
  }

  function handleClearFilters() {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(DEFAULT_PAGE);
  }

  function renderSkeletonCards() {
    return (
      <div className="row g-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="col-xl-4 col-lg-6">
            <Card className="border-0 shadow-sm h-100">
              <div className="card-body p-4">
                <div className="placeholder-glow">
                  <span className="placeholder col-7 mb-3"></span>
                  <span className="placeholder col-12 mb-2"></span>
                  <span className="placeholder col-10 mb-2"></span>
                  <span className="placeholder col-8"></span>
                </div>
              </div>
            </Card>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="row g-3 mb-4">
        {summaryMetrics.map((metric) => (
          <div key={metric.title} className="col-sm-6 col-xl-3">
            <InfrastructureSummaryCard {...metric} />
          </div>
        ))}
      </div>

      <div className="d-flex flex-column flex-xl-row justify-content-between gap-3 mb-3">
        <div>
          <h3 className="fw-bold mb-2">Infraestruturas</h3>
          <p className="text-muted mb-0">
            Visualize e gerencie suas infraestruturas em um só lugar.
          </p>
        </div>

        <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
          <small className="text-muted text-sm-end">
            {masks.formatMilhar(infrastructureList?.total || 0)} registros
          </small>

          <div style={{ minWidth: 170 }}>
            <Select
              className="mb-0"
              aria-label="Ordenar por"
              value={sortMode}
              onChange={(event) =>
                setSortMode(event.target.value as InfrastructureSortMode)
              }
            >
              <option value="name">Ordenar: Nome</option>
              <option value="status">Ordenar: Status</option>
              <option value="type">Ordenar: Tipo</option>
              <option value="host">Ordenar: Host</option>
              <option value="disk">Ordenar: Disco</option>
            </Select>
          </div>

          <div className="btn-group" role="group" aria-label="Modo de visualização">
            <button
              type="button"
              className={`btn d-inline-flex align-items-center gap-2 ${
                viewMode === "cards" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setViewMode("cards")}
            >
              <FiGrid />
              Cards
            </button>
            <button
              type="button"
              className={`btn d-inline-flex align-items-center gap-2 ${
                viewMode === "list" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setViewMode("list")}
            >
              <FiList />
              Lista
            </button>
          </div>

          <button
            type="button"
            className="btn btn-outline-primary d-inline-flex align-items-center justify-content-center gap-2"
            onClick={() => setShowFilters((currentValue) => !currentValue)}
          >
            <FaFilter />
            Filtros
            {showFilters ? <FiChevronUp /> : <FiChevronDown />}
          </button>

          <button
            type="button"
            className="btn btn-outline-primary d-inline-flex align-items-center justify-content-center gap-2"
            onClick={() => setAppliedFilters({ ...appliedFilters })}
            disabled={isLoading}
          >
            <FiRefreshCw />
            Atualizar
          </button>
        </div>
      </div>

      {showFilters && (
        <Card className="border-0 shadow-sm mb-4">
          <div className="card-body p-4">
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-lg-4">
                  <InputGroup
                    animated
                    label="Buscar"
                    placeholder="Nome, IP, host, região..."
                    icon={<FiSearch />}
                    value={filters.search}
                    onChange={(event) =>
                      handleFilterChange("search", event.target.value)
                    }
                  />
                </div>

                <div className="col-md-6 col-lg-2">
                  <Select
                    animated
                    label="Tipo"
                    value={filters.typeId}
                    onChange={(event) =>
                      handleSelectFilterChange("typeId", event.target.value)
                    }
                  >
                    <option value="">Todos</option>
                    {infraTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="col-md-6 col-lg-2">
                  <Select
                    animated
                    label="Host"
                    value={filters.hostId}
                    onChange={(event) =>
                      handleSelectFilterChange("hostId", event.target.value)
                    }
                  >
                    <option value="">Todos</option>
                    {infraHostOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="col-md-6 col-lg-2">
                  <Select
                    animated
                    label="Status"
                    value={filters.status}
                    onChange={(event) =>
                      handleSelectFilterChange("status", event.target.value)
                    }
                  >
                    <option value="">Todos</option>
                    {infrastructureStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="col-md-6 col-lg-2">
                  <Input
                    animated
                    label="Região"
                    placeholder="sa-east-1"
                    value={filters.region}
                    onChange={(event) =>
                      handleFilterChange("region", event.target.value)
                    }
                  />
                </div>

                <div className="col-md-6 col-lg-3">
                  <Input
                    animated
                    label="Tipo de instância"
                    placeholder="t3a.small, droplet..."
                    value={filters.instanceType}
                    onChange={(event) =>
                      handleFilterChange("instanceType", event.target.value)
                    }
                  />
                </div>

                <div className="col-6 col-md-3 col-lg-2">
                  <Input
                    animated
                    label="vCPU mín."
                    type="number"
                    min="0"
                    value={filters.minVCpu}
                    onChange={(event) =>
                      handleFilterChange("minVCpu", event.target.value)
                    }
                  />
                </div>

                <div className="col-6 col-md-3 col-lg-2">
                  <Input
                    animated
                    label="vCPU máx."
                    type="number"
                    min="0"
                    value={filters.maxVCpu}
                    onChange={(event) =>
                      handleFilterChange("maxVCpu", event.target.value)
                    }
                  />
                </div>

                <div className="col-6 col-md-3 col-lg-2">
                  <Input
                    animated
                    label="RAM mín. GB"
                    type="number"
                    min="0"
                    value={filters.minRamGb}
                    onChange={(event) =>
                      handleFilterChange("minRamGb", event.target.value)
                    }
                  />
                </div>

                <div className="col-6 col-md-3 col-lg-2">
                  <Input
                    animated
                    label="RAM máx. GB"
                    type="number"
                    min="0"
                    value={filters.maxRamGb}
                    onChange={(event) =>
                      handleFilterChange("maxRamGb", event.target.value)
                    }
                  />
                </div>

                <div className="col-6 col-md-3 col-lg-2">
                  <Input
                    animated
                    label="Disco mín."
                    type="number"
                    min="0"
                    value={filters.minDiskGb}
                    onChange={(event) =>
                      handleFilterChange("minDiskGb", event.target.value)
                    }
                  />
                </div>

                <div className="col-6 col-md-3 col-lg-2">
                  <Input
                    animated
                    label="Disco máx."
                    type="number"
                    min="0"
                    value={filters.maxDiskGb}
                    onChange={(event) =>
                      handleFilterChange("maxDiskGb", event.target.value)
                    }
                  />
                </div>
              </div>

              <div className="d-flex flex-column flex-sm-row justify-content-end gap-2 mt-3">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={handleClearFilters}
                  disabled={isLoading}
                >
                  Limpar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary d-inline-flex align-items-center justify-content-center gap-2"
                  disabled={isLoading}
                >
                  <FaFilter />
                  Filtrar
                </button>
              </div>
            </form>
          </div>
        </Card>
      )}

      {isLoading && renderSkeletonCards()}

      {!isLoading && sortedInfrastructures.length === 0 && (
        <Card className="border-0 shadow-sm">
          <div className="card-body text-center p-5">
            <FaMicrochip className="text-muted mb-3" size={34} />
            <h5 className="fw-bold">Nenhuma infraestrutura encontrada</h5>
            <p className="text-muted mb-0">
              Ajuste os filtros para localizar os ambientes cadastrados.
            </p>
          </div>
        </Card>
      )}

      {!isLoading && sortedInfrastructures.length > 0 && (
        <>
          {viewMode === "cards" && (
            <div className="row g-3">
              {sortedInfrastructures.map((infrastructure) => (
                <div key={infrastructure.id} className="col-xl-4 col-lg-6">
                  <InfrastructureCard infrastructure={infrastructure} />
                </div>
              ))}
            </div>
          )}

          {viewMode === "list" && (
            <InfrastructureTable infrastructures={sortedInfrastructures} />
          )}

          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mt-4">
            <small className="text-muted">
              Exibindo {masks.formatMilhar(sortedInfrastructures.length)} de{" "}
              {masks.formatMilhar(infrastructureList?.total || 0)} registros
            </small>
            <Pagination
              loading={isLoading}
              page={page}
              setPage={setPage}
              totalPages={infrastructureList?.totalPages || 1}
              maxPageShow={5}
            />
          </div>
        </>
      )}
    </>
  );
}
